import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@shared-core/data/api/errors";
import type { SupplierPortalDataAdapter } from "@shared-core/data/supplier-portal-adapter";
import type { AssessmentAnswers } from "@shared-core/domain/supplier-assessment";

/** How long the supplier stops typing before the answers are saved. */
export const AUTOSAVE_DELAY_MS = 800;

/**
 * Where saving stands: `saved` once the last change is kept, `saving` while one is on its way, `failed` when the
 * server could not be reached (try again), `stale` when another window saved first (reload to see its answers).
 */
export type SaveStatus = "idle" | "saving" | "saved" | "failed" | "stale";

/**
 * The answers of an assessment still with the supplier, saved as the supplier goes: changes wait a moment, then go in
 * one save that names the version they were loaded or last saved at. A change made while a save is on its way goes in
 * a second save from the new version, so saves never overlap and never name a version that is already out of date. A
 * 409 means another window saved first: nothing more is sent until the page reloads.
 */
export function useAssessmentAutosave({
  adapter,
  assessmentId,
  initial,
  isEnabled,
}: {
  adapter: SupplierPortalDataAdapter;
  assessmentId: string;
  initial: { answers: AssessmentAnswers; version: number };
  /** False for a submitted or past assessment: nothing is saved. */
  isEnabled: boolean;
}) {
  const [answers, setAnswersState] = useState(initial.answers);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const answersRef = useRef(initial.answers);
  const versionRef = useRef(initial.version);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSaving = useRef(false);
  const isDirty = useRef(false);
  const isStopped = useRef(false);

  const run = useCallback(async () => {
    if (isSaving.current || isStopped.current || !isDirty.current) return;
    isSaving.current = true;
    setStatus("saving");
    try {
      while (isDirty.current && !isStopped.current) {
        isDirty.current = false;
        try {
          versionRef.current = await adapter.saveAssessmentAnswers(
            assessmentId,
            answersRef.current,
            versionRef.current,
          );
        } catch (err) {
          isDirty.current = true;
          if (err instanceof ApiError && err.status === 409) {
            isStopped.current = true;
            setStatus("stale");
          } else {
            setStatus("failed");
          }
          return;
        }
      }
      if (!isStopped.current) setStatus("saved");
    } finally {
      isSaving.current = false;
    }
  }, [adapter, assessmentId]);

  const setAnswers = useCallback(
    (change: Partial<AssessmentAnswers>) => {
      const next = { ...answersRef.current, ...change };
      answersRef.current = next;
      setAnswersState(next);
      if (!isEnabled || isStopped.current) return;
      isDirty.current = true;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void run(), AUTOSAVE_DELAY_MS);
    },
    [isEnabled, run],
  );

  /** Try the failed save again now. */
  const retry = useCallback(() => void run(), [run]);

  /** The supplier submitted: nothing more is saved. */
  const stop = useCallback(() => {
    isStopped.current = true;
    if (timer.current) clearTimeout(timer.current);
  }, []);

  // Leaving the page keeps what was typed in the last moment.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      void run();
    },
    [run],
  );

  return { answers, setAnswers, status, retry, stop };
}
